import React from 'react';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import { compose } from 'app/blocks/common/utils';
import withScrollToTop from 'app/blocks/common/withScrollToTop';
import ErrorLabel from 'app/blocks/ErrorLabel/ErrorLabel';
import { BackAndForthButtons } from 'app/pages/license-signing-process/buttons';
import Context from 'app/pages/license-signing-process/context/Context';
import OnlineOpenPaymentContext from 'app/pages/license-signing-process/Payment/OnlineOpenPaymentContext';
import PaymentPanelEnum from 'app/pages/license-signing-process/Payment/PaymentPanelEnum';
import StepEnum from 'app/pages/license-signing-process/StepEnum';
import { SectionTitle } from 'app/pages/license-signing-process/styles';
import flow from 'app/pages/orders/orders.flow';
import CheckboxButton from 'app/ui/form/CheckboxButton';
import { BillingAddressReviewPanel } from './ContactReviewPanel';
import DiscountReviewPanel from './DiscountReviewPanel';
import PaymentAmountBreakdown from './PaymentAmountBreakdown';
import PaymentAmountReviewPanel from './PaymentAmountReviewPanel';
import PaymentReviewPanel from './PaymentReviewPanel';
import VatTaxReviewPanel from './VatTaxReviewPanel';

const isEmptyObject = obj => Object.keys(obj).length === 0;

const hasEmptyDiscountDetails = order => !order.discountDetails || isEmptyObject(order.discountDetails);

const shouldDisablePaymentDataCorrectCheckbox = order => order.pricingDetails && order.pricingDetails.pricesUnavailable;

const isWoaInstitutionalDiscount = discounts => Object.keys(discounts).includes('woaInstitutionName');

const shouldShowDiscountReviewPanel = order =>
    !hasEmptyDiscountDetails(order) && !isWoaInstitutionalDiscount(order.discountDetails);

class ReviewStep extends React.PureComponent {
    render() {
        const {
            cpsError,
            hasLicenseSubmissionStep,
            ignoredCpsErrors,
            initOrder,
            isCheckoutNeeded,
            isConfirming,
            isPaymentDataCorrect,
            isPreviewConfirmAllowed,
            isSocopayDataCorrect,
            l,
            onPaymentDataCorrectChange,
            onRequestEditOrder,
            onReviewCancel,
            onReviewConfirm,
            onSocopayAgreeChange,
            order,
            specificCpsErrors,
        } = this.props;

        const shouldShowCpsError = cpsError && !ignoredCpsErrors.includes(cpsError);

        return (
            <>
                <section className="payment payment--review">
                    {shouldShowCpsError && (
                        <div className="PaymentError">
                            <ErrorLabel>
                                <p className="text" data-seleniumid="reviewOrder-errorLabel">
                                    {l(
                                        `LICENSE_SIGNING.PAYMENT.CPS_ERRORS.${
                                            specificCpsErrors.includes(cpsError) ? cpsError : 'GENERIC'
                                        }`,
                                    )}
                                </p>
                            </ErrorLabel>
                        </div>
                    )}
                    <SectionTitle data-seleniumid="reviewOrder-blockTitle">
                        {l('LICENSE_SIGNING.PAYMENT.TITLE.REVIEW_ORDER')}
                    </SectionTitle>

                    <BillingAddressReviewPanel
                        initOrder={initOrder}
                        onEdit={() => onRequestEditOrder(PaymentPanelEnum.CONTACT)}
                        order={order}
                        panelName="ContactPanel"
                    />

                    {shouldShowDiscountReviewPanel(order) && (
                        <DiscountReviewPanel
                            initOrder={initOrder}
                            onEdit={() => onRequestEditOrder(PaymentPanelEnum.DISCOUNT)}
                            order={order}
                            panelName="DiscountPanel"
                        />
                    )}

                    <PaymentReviewPanel
                        initOrder={initOrder}
                        onEdit={() => onRequestEditOrder(PaymentPanelEnum.PAYMENT)}
                        order={order}
                        panelName="PaymentPanel"
                    />

                    <VatTaxReviewPanel
                        initOrder={initOrder}
                        onEdit={() => onRequestEditOrder(PaymentPanelEnum.CONTACT)}
                        order={order}
                        panelName="VatTaxPanel"
                    />

                    {(flow.isPaymentMethodSocopay(order) && (
                        <PaymentAmountBreakdown
                            chinaPayDetails={order.chinaPayPricingDetails}
                            isSocopayDataCorrect={isSocopayDataCorrect}
                            l={l}
                            onSocopayAgreeChange={onSocopayAgreeChange}
                            order={order}
                            panelName="PaymentAmountPanel"
                        />
                    )) || <PaymentAmountReviewPanel order={order} panelName="PaymentAmountPanel" />}

                    <CheckboxButton
                        checked={isPaymentDataCorrect}
                        className="payment-AcceptCheckBox"
                        data-seleniumid="paymentDataCorrectCheckbox"
                        disabled={shouldDisablePaymentDataCorrectCheckbox(order)}
                        id="paymentDataCorrectCheckbox"
                        label={l('LICENSE_SIGNING.PAYMENT.LABEL.DATA_CORRECT_CHECKBOX')}
                        onChange={event => onPaymentDataCorrectChange(event.target.checked)}
                    />

                    <div
                        className="payment-AcceptCheckBoxTip"
                        dangerouslySetInnerHTML={{ __html: l('LICENSE_SIGNING.PAYMENT.LABEL.DATA_CORRECT_TIP') }}
                        data-seleniumid="paymentDataCorrect-text"
                    />
                </section>

                <div className="licenseSigningProcessPage-backAndForth">
                    <BackAndForthButtons
                        buttonCancelLabel={l('BUTTONS.BACK')}
                        buttonConfirmingLabel={l('BUTTONS.PROCESSING')}
                        buttonConfirmLabel={
                            // eslint-disable-next-line no-nested-ternary
                            isCheckoutNeeded
                                ? flow.isPaymentMethodSocopay(order)
                                    ? l('LICENSE_SIGNING.BUTTON.GO_TO_PAYMENT')
                                    : l('LICENSE_SIGNING.BUTTON.GO_TO_CHECKOUT')
                                : hasLicenseSubmissionStep
                                ? l('LICENSE_SIGNING.BUTTON.GO_TO_LICENSE_SIGNING')
                                : l('LICENSE_SIGNING.BUTTON.SUBMIT_ORDER_AND_FINISH')
                        }
                        canConfirm={isPreviewConfirmAllowed && !isConfirming}
                        onCancel={onReviewCancel}
                        onConfirm={onReviewConfirm}
                    />
                </div>
            </>
        );
    }
}

export { ReviewStep };
export default compose(
    withScrollToTop,
    Context.withContext(state => ({
        hasLicenseSubmissionStep: !!state.steps.find(x => x.id === StepEnum.LICENSE_SUBMISSION),
    })),
    OnlineOpenPaymentContext.withContext(state => ({
        initOrder: state.initOrder,
        isCheckoutNeeded: state.isCheckoutNeeded,
        isConfirming: state.review.isConfirming,
        isPaymentDataCorrect: state.review.isPaymentDataCorrect,
        isPreviewConfirmAllowed:
            !!state.review.isPaymentDataCorrect &&
            (!flow.isPaymentMethodSocopay(state.order) || !!state.review.isSocopayDataCorrect),
        isSocopayDataCorrect: state.review.isSocopayDataCorrect,
        onReviewCancel: state.onReviewCancel,
        onReviewConfirm: state.onReviewConfirm,
        onPaymentDataCorrectChange: state.onPaymentDataCorrectChange,
        onSocopayAgreeChange: state.onSocopayAgreeChange,
        onRequestEditOrder: state.onRequestEditOrder,
        order: state.order,
        cpsError: state.cpsError,
        ignoredCpsErrors: state.ignoredCpsErrors,
        specificCpsErrors: state.specificCpsErrors,
    })),
    withCodes2(ID.BUTTONS, ID.LICENSE_SIGNING),
)(ReviewStep);
